// Per-channel disappearing-message timer setting.
//
// Ported from DisappearingMessageStore.swift. Storage is keyed by a SHA-256
// hash of the channelId so unfortunate channel ids never collide with
// IndexedDB key-space. Default for any unknown channel is `.off`, matching
// Signal's "off until the user enables it" behaviour.
//
// In Signal, timer changes are themselves a system message and replicate
// across the conversation. This store is the *local* mirror of that state;
// when a TimerUpdate control message arrives over the wire, the receive path
// calls `setTimer` to reconcile. When the local user changes the timer, the
// send path emits a control message AND calls `setTimer`.
//
// Web storage: IndexedDB (via raw indexedDB API, no idb-keyval dependency so
// we can control the DB name precisely for test isolation).

import { DisappearingMessageDuration } from "./DisappearingMessageDuration";

export interface ChannelTimerRecord {
  readonly channelId: string;
  readonly durationSeconds: number;
  readonly updatedAt: number; // ms since epoch
  readonly updatedByUserId: string | null; // null = local change
}

const DEFAULT_DB_NAME = "kaether-disappearing-timers";
const STORE_NAME = "timers";
const DB_VERSION = 1;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  const arr = new Uint8Array(hash);
  let hex = "";
  for (let i = 0; i < arr.length; i++) {
    hex += arr[i].toString(16).padStart(2, "0");
  }
  return hex;
}

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export class DisappearingMessageStore {
  private readonly dbName: string;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  // -------------------------------------------------------------------------
  // API

  async setTimer(
    channelId: string,
    duration: DisappearingMessageDuration,
    updatedByUserId: string | null = null,
    at: Date = new Date(),
  ): Promise<ChannelTimerRecord> {
    const record: ChannelTimerRecord = {
      channelId,
      durationSeconds: duration.seconds,
      updatedAt: at.getTime(),
      updatedByUserId,
    };
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).put(record, key);
    });
    db.close();
    return record;
  }

  async getTimer(channelId: string): Promise<DisappearingMessageDuration> {
    const record = await this.record(channelId);
    return record
      ? DisappearingMessageDuration.fromSeconds(record.durationSeconds)
      : DisappearingMessageDuration.off;
  }

  async record(channelId: string): Promise<ChannelTimerRecord | null> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    return new Promise<ChannelTimerRecord | null>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(key);
      tx.oncomplete = () => {
        db.close();
        resolve((req.result as ChannelTimerRecord) ?? null);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  async allRecords(): Promise<ChannelTimerRecord[]> {
    const db = await openDb(this.dbName);
    return new Promise<ChannelTimerRecord[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).getAll();
      tx.oncomplete = () => {
        db.close();
        resolve((req.result as ChannelTimerRecord[]) ?? []);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  }

  async reset(channelId: string): Promise<void> {
    const key = await sha256Hex(channelId);
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).delete(key);
    });
    db.close();
  }

  async resetAll(): Promise<void> {
    const db = await openDb(this.dbName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE_NAME).clear();
    });
    db.close();
  }
}
